import { DataTable, type DataTableColumn, type DataTablePagination } from '@atrvd/saas-ui'
import type { Organization } from '@/hooks/useOrganizationInvitations'
import { formatDate } from '@dcp/shared/utils/formatters'
import { queryErrorDisplay, type TableQueryStateProps } from '@/components/common/queryErrorDisplay'

interface OrganizationsTableProps extends TableQueryStateProps {
  /** Rows of the visible page, paged by the owner. */
  organizations: Organization[]
  /**
   * The footer, owned by the page (`useTablePagination().forTotal`); `undefined`
   * hides it (≤ 20 organizations, or the total not known yet).
   */
  pagination: DataTablePagination | undefined
}

const COLUMNS: DataTableColumn<Organization>[] = [
  { key: 'name', header: 'Organization Name', render: (org) => org.name, cellClassName: 'font-medium' },
  { key: 'admin_email', header: 'Admin Email', render: (org) => org.admin_email || '—' },
  { key: 'admin_name', header: 'Admin Name', render: (org) => org.admin_name || '—' },
  { key: 'created_at', header: 'Created', render: (org) => formatDate(org.created_at) },
]

/** Admin list of tenant organizations with their admin contact and creation date. */
export function OrganizationsTable({
  organizations,
  pagination,
  loading,
  refreshing,
  error,
  retrying,
  onRetry,
}: OrganizationsTableProps) {
  const { banner, tableError } = queryErrorDisplay({ error, retrying, onRetry }, organizations.length)
  return (
    <>
      {banner}
      <DataTable
        columns={COLUMNS}
        data={organizations}
        rowKey={(org) => org.id}
        pagination={pagination}
        itemName="organizations"
        loading={loading}
        refreshing={refreshing}
        // The footer stays with a failed page, so the user can step back to one that loaded.
        error={tableError}
        emptyState={
          <p className="text-center text-muted-foreground">
            No organizations found. Create one via the Invitations page.
          </p>
        }
      />
    </>
  )
}
